import { useDeclineBooking } from '../api/queries'
import { fmtDate, fmtRange } from '../lib/time'
import { Dialog } from './Dialog'
import { ErrorNotice } from './ErrorNotice'
import { Button } from './ui'

/**
 * Подтверждение выхода из встречи.
 *
 * «Не смогу прийти» — необратимое действие с последствиями за пределами экрана:
 * организатору уходит письмо, участника вычёркивают из списка, а кнопка после
 * этого исчезает навсегда — вернуться в участники самому нечем (в API есть
 * только decline). Раньше всё это происходило по одному нажатию, причём кнопка
 * стояла в строке списка рядом с безобидным «Открыть»: промах пальцем на телефоне
 * стоил письма организатору и места на встрече.
 *
 * Отсюда подтверждение — и в нём названы обе вещи, которые человек хочет знать
 * до нажатия: что уйдёт письмо и что передумать самостоятельно не выйдет.
 */
export function DeclineDialog({
  booking,
  onClose,
  onDeclined,
}: {
  booking: { id: number; title?: string; starts_at: string; ends_at: string }
  onClose: () => void
  /** Вызывается после отказа. Список закрывает карточку, страница встречи остаётся. */
  onDeclined?: () => void
}) {
  const decline = useDeclineBooking()

  return (
    <Dialog
      open
      onClose={onClose}
      title="Сказать, что не сможете прийти?"
      description={
        <span className="tnum">
          {booking.title || 'Занято'} · {fmtDate(booking.starts_at)},{' '}
          {fmtRange(booking.starts_at, booking.ends_at)}
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Остаться в участниках
          </Button>
          <Button
            variant="danger"
            loading={decline.isPending}
            onClick={() =>
              decline.mutate(booking.id, {
                onSuccess: () => {
                  onDeclined?.()
                  onClose()
                },
              })
            }
          >
            Не смогу прийти
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm">
          Организатору уйдёт письмо, а вас вычеркнут из списка участников. Встреча при этом
          останется: комната занята под неё.
        </p>
        <p className="hint">
          Вернуться в участники самостоятельно не получится — если планы поменяются,
          попросите организатора позвать вас снова.
        </p>
        {decline.error && <ErrorNotice error={decline.error} />}
      </div>
    </Dialog>
  )
}
